/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */

/* eslint-disable jsx-a11y/click-events-have-key-events */
import { Typography } from '@remarkable/ark-web';
import { getRouteApi } from '@tanstack/react-router';
import {
  functionalUpdate,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useMemo, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import type { FleetDeviceWithMember } from 'src/api/endpoints/cloudEnterprise.types';
import { useFleetDevices } from 'src/api/queries/fleet';
import { Button, Input, SomethingWentWrong, Table } from 'src/components';
import { useMultiRowSelect } from 'src/utils/createTableSelectColumn';
import { tableHelpers } from 'src/utils/tableHelpers';

import { ChevronRight, Import, X } from '@remarkable/ark-icons';
import { formatDistanceToNow } from 'date-fns';
import { ButtonClicked } from 'src/ampli';
import { tracker } from 'src/analytics/tracker';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import { useAccessTokenClaims } from 'src/api/queries/user';
import { CopyButton } from 'src/components/CopyButton';
import { DeviceName } from 'src/components/DeviceName';
import { TableBulkActionsPanel } from 'src/components/TableBulkActionsPanel';
import { deviceHelpers } from 'src/utils/deviceHelpers';
import { downloadCSV } from 'src/utils/downloadCsv';
import { userHelpers } from 'src/utils/userHelpers';
import { DevicesTableBody } from './DevicesTableBody';
import { deviceTableColumns } from './deviceTableColumns';
import { FleetTableColumnFilterSelector } from './FleetTableColumnFilterSelector';
import { fleetUserHelpers } from './fleetUserHelpers';

const routeApi = getRouteApi('/_auth/enterprise/_with-org/_layout/devices');

export const DevicesTable = () => {
  const devices = useFleetDevices();
  const accessTokenClaims = useAccessTokenClaims();
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();

  const columnFilters = useMemo(() => {
    return tableHelpers.paramsToFilters({
      devices: search.devices,
      'device-software-versions': search['device-software-versions'],
      compliance: search.compliance,
      'device-verified': search['device-verified'],
    });
  }, [
    search.devices,
    search['device-software-versions'],
    search.compliance,
    search['device-verified'],
  ]);

  const debouncedGlobalFilter = useDebouncedCallback((value: string) => {
    void navigate({
      to: '.',
      search: { ...search, filter: value.trim() || undefined },
      replace: true,
    });
  }, 100);

  const tableRef = useRef(null);

  const table = useReactTable({
    data: devices.data || [],
    columns: deviceTableColumns,
    initialState: {
      pagination: {
        pageSize: 25,
      },
    },
    state: {
      globalFilter: search.filter?.trim() || undefined,
      columnFilters,
      columnVisibility: {
        status: false,
        edit: userHelpers.hasPermissionCreateCommands(accessTokenClaims.data),
        'device-mac-address': false,
      },
    },
    onGlobalFilterChange: (updater: string | undefined) => {
      void navigate({
        to: '.',
        search: (prev) => ({
          ...prev,
          filter: functionalUpdate(updater, prev.filter)?.trim() || undefined,
        }),
        replace: true,
      });
    },
    onColumnFiltersChange: (updater) => {
      const newColumnFilters = functionalUpdate(updater, columnFilters);

      const filterParams = tableHelpers.filtersToParams(newColumnFilters, {
        devices: undefined,
        integrations: undefined,
        'device-software-versions': undefined, // needs more tweaking
        compliance: undefined,
        'device-verified': undefined,
      });

      void navigate({
        to: '.',
        search: (prev) => ({
          ...prev,
          ...filterParams,
        }),
        replace: true,
      });
    },
    meta: {
      ...useMultiRowSelect<FleetDeviceWithMember>(),
      fillRowsToMatchPageSize: false,
      getRowLabel: (data) => deviceHelpers.getDeviceName(data.device),
      getRowDescription: () => 'Press Enter to open the device details',
    },
    enableFilters: true,
    getRowId: (data, index) => data.key || index.toString(),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getPaginationRowModel: getPaginationRowModel(),
    renderFallbackValue: '-',
  });

  if (devices.isError) {
    return <SomethingWentWrong error={devices.error} />;
  }

  return (
    <div className="flex flex-col gap-16 py-16">
      <div className="flex flex-wrap-reverse items-end justify-between gap-16">
        <div className="flex w-full flex-col gap-8 ls:w-auto">
          <div className="flex w-full items-center gap-8 ls:w-auto">
            <Input
              id="search"
              placeholder="Search"
              className="w-full ls:max-w-[300px]"
              defaultValue={search.filter}
              onChange={(e) => debouncedGlobalFilter(e.currentTarget.value)}
            />
            <FleetTableColumnFilterSelector
              componentLocation={ComponentLocations.ENTERPRISE.DEVICES_PAGE}
              data-cy="device-table-filters-button"
              table={table}
              columns={[
                'devices',
                'device-software-versions',
                'compliance',
                'device-verified',
              ].filter(Boolean)}
            />
          </div>

          <div className="rounded-sm px-8 py-4">
            <Typography
              variant="interface-sm"
              leadingNone
              uppercase
              className="opacity-70"
            >
              {table.getFilteredRowModel().flatRows.length} rows
            </Typography>
          </div>
        </div>
      </div>
      <div ref={tableRef} className="w-full">
        <Table
          table={table}
          data-cy="devices-table"
          className="hidden w-full lxl:table"
          loading={devices.isPending}
          pagination
        >
          <DevicesTableBody table={table} />
        </Table>
      </div>
      <ul
        data-cy="members-list-mobile"
        className="border-t border-gray-100 lxl:hidden"
      >
        {table.getRowModel().rows.map((row) => (
          <div key={row.id}>
            <li
              data-cy={row.id}
              className="cursor-pointer border-b border-gray-100 py-12 hover:bg-paper-3"
              onClick={() => {
                if (search.device !== row.original.device.serialNumber) {
                  void navigate({
                    search: { device: row.original.device.serialNumber },
                    replace: true,
                  });
                } else {
                  void navigate({
                    search: { device: undefined },
                    replace: true,
                  });
                }
              }}
            >
              <div className="flex items-center justify-between px-8">
                <div className="mr-8 flex w-fit shrink grow flex-col truncate">
                  <Typography variant="body-sm" className="truncate">
                    <DeviceName device={row.original.device} />
                  </Typography>
                  <Typography
                    variant="interface-xs"
                    leadingNone
                    className="text-neutral-dark-1"
                  >
                    Last seen:{' '}
                    <span>
                      {formatDistanceToNow(
                        new Date(row.original.device.lastSeen ?? ''),
                        { addSuffix: true }
                      )}
                    </span>
                  </Typography>
                  <Typography
                    variant="interface-xs"
                    leadingNone
                    className="text-neutral-dark-1"
                  >
                    <span className="text-muted">
                      {fleetUserHelpers.getName(row.original.user)}
                    </span>
                  </Typography>
                </div>

                <ChevronRight
                  size={20}
                  className="shrink-0 text-neutral-dark-1"
                />
              </div>
            </li>
          </div>
        ))}
      </ul>
      <TableBulkActionsPanel
        table={table}
        className="flex justify-between px-16"
      >
        <div className="flex items-center gap-16">
          <Typography variant="interface-sm" leadingNone uppercase>
            {table.getSelectedRowModel().rows.length} devices selected
          </Typography>
        </div>

        <div className="flex items-center gap-16">
          <CopyButton
            size="medium"
            variant="tertiary-neutral"
            value={table
              .getSelectedRowModel()
              .flatRows.map((r) => r.original.device.serialNumber)
              .join('\n')}
            className="flex-row-reverse"
            onClick={() => {
              tracker.trackEvent(
                new ButtonClicked({
                  component_location:
                    ComponentLocations.ENTERPRISE.DEVICES_BULK_ACTIONS,
                  text: 'Copy serial numbers',
                  action: 'copy selected device serial numbers',
                })
              );
            }}
          >
            Copy serial numbers
          </CopyButton>

          <Button
            size="medium"
            variant="secondary"
            analytics={{
              location: ComponentLocations.ENTERPRISE.DEVICES_BULK_ACTIONS,
              text: 'Download CSV',
              action: 'download selected devices as csv',
            }}
            onClick={() => {
              const selectedRows = table
                .getSelectedRowModel()
                .flatRows.map((r) => {
                  const Type = deviceHelpers.getDeviceName(r.original.device);
                  const SerialNumber = r.original.device.serialNumber || '';
                  const SoftwareVersion =
                    r.original.device.softwareVersion || '';
                  const PasscodeLength = r.original.device.passcodeDigits || '';
                  const Email = r.original.user.email;
                  const Name = fleetUserHelpers.getName(r.original.user);
                  const Compliance = fleetUserHelpers.getDeviceComplianceStatus(
                    r.original.device
                  );
                  const Verified = fleetUserHelpers.getDeviceVerificationStatus(
                    r.original.device
                  );

                  const MacAddress = r.original.device.macAddress || '';

                  return {
                    Type,
                    SerialNumber,
                    SoftwareVersion,
                    PasscodeLength,
                    Compliance,
                    Verified,
                    Email,
                    Name: Name !== Email ? Name : '',
                    MacAddress,
                  };
                });

              downloadCSV(selectedRows, 'devices.csv', ',');
            }}
          >
            <Import /> <span>Download CSV</span>
          </Button>

          <Button
            variant="tertiary-neutral"
            size="medium"
            onClick={() => {
              table.setRowSelection({});
            }}
            analytics={{
              location: ComponentLocations.ENTERPRISE.DEVICES_BULK_ACTIONS,
              text: '[X icon]',
              action: 'close bulk actions panel',
            }}
            className="!rounded"
          >
            <X />
          </Button>
        </div>
      </TableBulkActionsPanel>
    </div>
  );
};
